import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProdutoGradeRow {
  id: number;
  gradeXId: number;
  gradeXDescricao?: string | null;
  gradeYId: number;
  gradeYDescricao?: string | null;
  codigoBarras?: string | null;
  isAtivo: boolean;
}

export interface SalvarProdutoGradeRequest {
  gradeXId: number;
  gradeYId: number;
  codigoBarras?: string | null;
  isAtivo: boolean;
}

export const useProdutoGrades = (produtoId: string | undefined) => {
  const qc = useQueryClient();
  const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-grades", numId];
  const base = `/api/produtos/${numId}/grades`;

  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await apiClient.get<ProdutoGradeRow[]>(base)).data,
    enabled: !!numId,
  });

  const create = useMutation({
    mutationFn: async (body: SalvarProdutoGradeRequest) => (await apiClient.post<ProdutoGradeRow>(base, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Combinação de grade adicionada!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
  const update = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SalvarProdutoGradeRequest }) =>
      (await apiClient.put<ProdutoGradeRow>(`${base}/${id}`, data)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Grade atualizada!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
  const remove = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`${base}/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Combinação removida!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
  const gerar = useMutation({
    mutationFn: async (body: { gradeXIds: number[]; gradeYIds: number[] }) =>
      (await apiClient.post<ProdutoGradeRow[]>(`${base}/gerar`, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Combinações geradas!"); },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });

  return {
    grades: query.data ?? [],
    createGrade: create.mutate,
    updateGrade: update.mutate,
    deleteGrade: remove.mutate,
    gerarGrades: gerar.mutate,
    isGerando: gerar.isPending,
  };
};
